/** @format */

import {ChangeEvent, useEffect, useState} from 'react';

import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import SearchIcon from '@mui/icons-material/Search';
import {Box, InputAdornment, LinearProgress, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {format} from 'date-fns';

import {useAuthContext} from 'contexts/AuthContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import LegacyTextField from 'components/inputs/LegacyTextField';
import RestrictionBlock from 'components/RestrictionBlock';
import MainLayoutNew from 'layouts/MainLayoutNew';

import {AI_MONITORING_BANNER_STORAGE_KEY} from 'components/dialogs/AiMonitoringBannerDialog/constants';

import AiMonitoringBannerDialog from 'components/dialogs/AiMonitoringBannerDialog';

import {CategorySelector} from './components/CategorySelector';
import {ProjectGroup} from './components/ProjectGroup';
import {filterCompaniesBySearchString} from './helpers/filterCompaniesBySearchString';
import {getCategories} from './helpers/getCategories';
import {mapCompanies} from './helpers/mapCompanies';
import {HomeGroupingClasses as classes, HomeGroupingRoot as Root} from './styles';
import {CategoriesFromQuery, CompanyFromQuery, GroupType} from './types';
import {useGetCurrentUserQuery} from './queries/__generated__';

const now = new Date();

const HomeGrouping = () => {
  const [chosenFiltering, setChosenFiltering] = useState<GroupType>('default');
  const [categories, setCategories] = useState<CategoriesFromQuery[]>([]);
  const [search, setSearch] = useState<string>('');
  const [companies, setCompanies] = useState<ReadonlyArray<CompanyFromQuery>>([]);
  const [showBannerDialog, setShowBannerDialog] = useState<boolean>(false);

  const {authToken} = useAuthContext();
  const {t} = useTranslation();
  const {mixpanel: untypedMixpanel} = useMixpanelContext();
  const navigate = useNavigate();

  const mixpanel = untypedMixpanel as typeof untypedMixpanel & {
    get_distinct_id: () => string;
  };

  const {data, loading} = useGetCurrentUserQuery({
    skip: !authToken,
    variables: {
      date: format(now, 'yyyy-MM-dd'),
    },
  });

  // Проверяем, нужно ли показать баннер при загрузке компонента
  useEffect(() => {
    const bannerViewed = localStorage.getItem(AI_MONITORING_BANNER_STORAGE_KEY);
    if (!bannerViewed || bannerViewed !== 'true') {
      setShowBannerDialog(true);
    }
  }, []);

  useEffect(() => {
    if (!data?.currentUser?.companies) return;

    const companiesData = data.currentUser.companies;
    setCompanies(filterCompaniesBySearchString(companiesData, search));
    setCategories(getCategories(companiesData));
  }, [data, search]);

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleClickAddNew = () => {
    try {
      mixpanel.track('click_home_new_add_company_button');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
    }

    navigate('/home/project/wizard/new');
  };

  const handleCloseBannerDialog = () => {
    setShowBannerDialog(false);
  };

  const isRestricted =
    !loading && data?.currentUser?.restrictions.projectCreationRestricted === true;

  const mappedCompanies = mapCompanies({
    data: companies,
    chosenFiltering,
    categories,
  });

  return (
    <Root>
      <MainLayoutNew>
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            <Trans>Проекты</Trans>
          </Typography>

          <Box className={classes.controlPanel}>
            <CategorySelector
              onChange={setChosenFiltering}
              categories={categories}
              chosenFiltering={chosenFiltering}
            />

            <LegacyTextField
              placeholder={t('Поиск', 'Поиск')}
              slotProps={{
                htmlInput: {
                  name: 'search',
                  type: 'search',
                },
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon htmlColor='#8B8B8B' />
                    </InputAdornment>
                  ),
                },
              }}
              onChange={handleChangeSearch}
              className={classes.searchInput}
            />
          </Box>

          <PrimaryMediumButton
            variant='contained'
            onClick={handleClickAddNew}
            className={
              isRestricted ? `${classes.addButton} ${classes.addButtonDisabled}` : classes.addButton
            }
            data-testid='add-project'
            disabled={isRestricted}
          >
            <Box
              sx={{
                mr: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <AddCircleOutlineRounded className={classes.addButtonIcon} />
            </Box>
            <Trans>Добавить проект</Trans>
          </PrimaryMediumButton>
        </Box>

        {isRestricted && <RestrictionBlock sx={{mt: 4}} />}

        {loading ? (
          <Box className={classes.loader} data-testid='loading'>
            <LinearProgress />
          </Box>
        ) : (
          <Box className={classes.projectGroups}>
            {mappedCompanies.map(group => (
              <ProjectGroup key={group.id} mappedData={group} originalCompanies={companies} />
            ))}
          </Box>
        )}
      </MainLayoutNew>

      <AiMonitoringBannerDialog open={showBannerDialog} onClose={handleCloseBannerDialog} />
    </Root>
  );
};

export default HomeGrouping;
